/**
 * What a file to import may be, per kind (decision 56, revised by the user's choice of 2026-10-08; part 17 log, table L):
 * a DOCX or Markdown file keeps decision 56's limits; a bundle may be anything the app's own export writes (part 24a).
 */

/**
 * The kinds of file the app imports: Markdown, plain text and DOCX (decision 40, part 18) and the bundle (decision 7,
 * part 24).
 */
export type ImportFormat = 'markdown' | 'text' | 'docx' | 'bundle';

/** Every kind, in table L's order. */
export const IMPORT_FORMATS: readonly ImportFormat[] = Object.freeze(['markdown', 'text', 'docx', 'bundle']);

/** What an archive's directory and reading may come to (table L's last three columns). */
export interface ArchiveLimits {
  /** The most entries its directory may list, directories included. */
  readonly files: number;
  /**
   * The most bytes its central directory may have (deep-fix r2): what the reader holds for the names before any file is
   * read, and what the worker's reply lists.
   */
  readonly directoryBytes: number;
  /** The most bytes one file may have unzipped. */
  readonly fileBytes: number;
  /** The most bytes all its files may have unzipped, declared and read (the read budget). */
  readonly totalBytes: number;
}

/** One kind's limits: the file's size, and the archive's when the kind is one. */
export interface ImportLimits {
  readonly inputBytes: number;
  readonly archive: ArchiveLimits | null;
}

/** L1, L2: decision 56's 50 MB input limit, in MiB as the image import counts it (part 16a, L1). */
export const MAX_IMPORT_BYTES = 50 * 1024 * 1024;

/** L2: decision 56's 200 MB unzipped. */
export const MAX_UNZIPPED_BYTES = 200 * 1024 * 1024;

/**
 * L3: the longest bundle part 24a's writer makes: its last offset (`MAX_ZIP_OFFSET`, 0xFFFFFFFE, Z4) plus its largest end
 * records (the ZIP64 record and locator and the classic record, 98 bytes, Z3).
 */
export const MAX_BUNDLE_BYTES = 0xffff_fffe + 98;

/** L2: a Word file holds a few hundred parts at most, a picture each; 16 MiB of directory gives them 1.6 KiB a record. */
export const DOCX_ARCHIVE: ArchiveLimits = Object.freeze({ files: 10_000, directoryBytes: 16 * 1024 * 1024, fileBytes: MAX_UNZIPPED_BYTES, totalBytes: MAX_UNZIPPED_BYTES });

/**
 * L3: a manifest listing 2^19 files stays under one file's 256 MiB; prose and JSON deflate far less than 8 to 1. Part
 * 24a's longest path is 78 bytes (`snapshots/<id>/<id>.md`) and its records hold no extra field or comment, so a record
 * is at most 124 bytes: 128 bytes a file for the directory, 64 MiB.
 */
export const BUNDLE_ARCHIVE: ArchiveLimits = Object.freeze({ files: 2 ** 19, directoryBytes: 2 ** 19 * 128, fileBytes: 256 * 1024 * 1024, totalBytes: 8 * MAX_BUNDLE_BYTES });

/** Table L, per kind. */
export const IMPORT_LIMITS: Readonly<Record<ImportFormat, ImportLimits>> = Object.freeze({
  // L1: one text file, never an archive.
  markdown: Object.freeze({ inputBytes: MAX_IMPORT_BYTES, archive: null }),
  // Part 18, deep-fix r1: a plain text file, as Markdown's.
  text: Object.freeze({ inputBytes: MAX_IMPORT_BYTES, archive: null }),
  docx: Object.freeze({ inputBytes: MAX_IMPORT_BYTES, archive: DOCX_ARCHIVE }),
  bundle: Object.freeze({ inputBytes: MAX_BUNDLE_BYTES, archive: BUNDLE_ARCHIVE }),
});
